import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Container,
  IconButton,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import FormikForm from 'src/ui-component/FormikFormLibrary';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { Fields } from '../../../ui-component/FormikFormLibrary/FormikTypes';
import { httpRequest } from 'src/services/httpRequest';
import { handleError } from 'src/redux-store/slices/errorSlice';
import { useDispatch, useSelector } from 'react-redux';
import PdfGenerator from 'src/ui-component/PdfGenerator';
import FormHeaderGreeting from 'src/components/Greeting/FormHeaderGreeting';
import Breadcrumb from 'src/ui-component/Breadcrumb';
import InfoIcon from '@mui/icons-material/Info';
import LoadingOverlay from 'src/components/common/LoadingOverlay';
import { RootState } from 'src/redux-store/appStore';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import CustomModal from 'src/ui-component/customModal/CustomModal';
import UploadDocs from '../../../ui-component/UploadDocs';
import Content from './Content';
import { getFeesForCurrentPermit } from '../common/getFeesForCurrentPermit';
import {
  PaymentApplicationDetails,
  PaymentSummary
} from 'src/types/RoadPermit/roadPermit.type';
import { sendNotification } from 'src/utils/sendNotification';
import { getApplicationDetails } from '../common/getApplicationDetails';
import { DraftCheck } from 'src/types/PZPermit/pzPermit.type';

let editData: any;
const NewApplication: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const currentPermitRole = useSelector(
    (state: RootState) =>
      state?.currentPermit?.currentPermitDetails?.currentPermitUserRole
  );
  const socket = useSelector((store: RootState) => store.socket.socket);
  const isManager = currentPermitRole === 'Manager';
  const { townshipId, permit, applicationID } = useParams();
  const [openApprovePopup, setOpenApprovePopup] = useState<boolean>(false);
  const userData = useSelector((store: RootState) => store.user.userDetails);
  const [loading, setLoading] = useState<boolean>(false);
  const [docsErrorMessege, setDocsErrorMessege] = useState<string>('');
  const [cloneData, setCloneData] = useState<Fields[]>([]);
  const [IsOpen, setIsOpen] = useState<boolean>(false);
  const [file, setFile] = useState<[]>([]);
  const [clickedOn, setClickedOn] = useState<string>('');
  const [switchTo, setSwitchTo] = useState<string>('');
  const [feeDetails, setFeeDetails] = useState({
    applicationFee: 0,
    inspectionFee: 0
  });
  const [taxRate, setTaxRate] = useState<number>(0);
  const formRef = useRef(null);

  // Get application details  
  const handleGetApplicationDetails = async () => {
    const isDraft = location?.state && (location?.state as DraftCheck)?.isDraft;
    const response = await getApplicationDetails({
      applicationID,
      permit,
      isDraft
    });
    if (response?.status === 'success') {
      const data = response?.data?.data[0];
      if (data?.VehicleUsed) {
        setCloneData([
          {
            initialValue: data?.VehicleYear || '',
            fieldName: 'VehicleYear',
            fieldLabel: 'Year of Vehicle',
            fieldType: 'number',
            required: true
          },
          {
            initialValue: data?.VehicleManufactured || '',
            fieldName: 'VehicleManufactured',
            fieldLabel: 'Make of Vehicle',
            fieldType: 'number',
            required: true
          },
          {
            initialValue: data?.VehicleModel || '',
            fieldName: 'VehicleModel',
            fieldLabel: 'Model of Vehicle',
            fieldType: 'text',
            required: true
          },
          {
            initialValue: data?.VehicleType || '',
            fieldName: 'VehicleType',
            fieldLabel: 'Type of Vehicle',
            fieldType: 'text',
            required: true
          },
          {
            initialValue: data?.VehicleColor || '',
            fieldName: 'VehicleColor',
            fieldLabel: 'Color of Vehicle',
            fieldType: 'text',
            required: true
          },
          {
            initialValue: data?.VehicleLisensePlateNumber || '',
            fieldName: 'VehicleLisensePlateNumber',
            fieldLabel: 'License Plate Number',
            fieldType: 'text',
            required: true
          }
        ]);
      }
      editData = data;
    }
  };

  useEffect(() => {
    handleGetApplicationDetails();
  }, []);

  // Handle dependency to show additional  vhicle fields
  function handleDependency(val: string) {
    if (val === 'Yes') {
      const mergedData = { ...editData, ...formRef.current.values };
      mergedData.VehicleUsed = true;
      editData = mergedData;
      setCloneData([
        {
          initialValue: '',
          fieldName: 'VehicleYear',
          fieldLabel: 'Year of Vehicle',
          fieldType: 'number',
          required: true
        },
        {
          initialValue: '',
          fieldName: 'VehicleManufactured',
          fieldLabel: 'Make of Vehicle',
          fieldType: 'number',
          required: true
        },
        {
          initialValue: '',
          fieldName: 'VehicleModel',
          fieldLabel: 'Model of Vehicle',
          fieldType: 'text',
          required: true
        },
        {
          initialValue: '',
          fieldName: 'VehicleType',
          fieldLabel: 'Type of Vehicle',
          fieldType: 'text',
          required: true
        },
        {
          initialValue: '',
          fieldName: 'VehicleColor',
          fieldLabel: 'Color of Vehicle',
          fieldType: 'text',
          required: true
        },
        {
          initialValue: '',
          fieldName: 'VehicleLisensePlateNumber',
          fieldLabel: 'License Plate Number',
          fieldType: 'text',
          required: true
        }
      ]);
    } else {
      const mergedData = { ...editData, ...formRef.current.values };
      mergedData.VehicleUsed = false;
      editData = mergedData;
      setCloneData([]);
    }
  }

  // All fields for the form using Formik
  const allFields: Fields[] = [
    {
      initialValue: editData?.ApplicantName || '',
      fieldName: 'ApplicantName',
      fieldLabel: 'Applicant Name',
      fieldType: 'text',
      required: true
    },
    {
      initialValue: editData?.ApplicantPhoneNo || '',
      fieldName: 'ApplicantPhoneNo',
      fieldLabel: 'Applicant Phone Number',
      fieldType: 'mobile',
      required: true
    },
    {
      initialValue: editData?.ApplicantAddress || '',
      fieldName: 'ApplicantAddress',
      fieldLabel: 'Applicant Address',
      fieldType: 'note'
    },
    {
      initialValue: editData?.CompanyAddress || '',
      fieldName: 'CompanyAddress',
      fieldLabel: 'Company Address ',
      fieldType: 'note',
      required: true
    },
    {
      initialValue: editData?.CompanyName || '',
      fieldName: 'CompanyName',
      fieldLabel: 'Company Name',
      fieldType: 'text',
      required: true
    },
    {
      initialValue: editData?.CompanyEmail || '',
      fieldName: 'CompanyEmail',
      fieldLabel: 'Company E-mail',
      fieldType: 'email',
      required: true
    },
    {
      initialValue: editData?.CompanyPhoneNumber || '',
      fieldName: 'CompanyPhoneNumber',
      fieldLabel: 'Company Phone Number',
      fieldType: 'mobile',
      required: true
    },
    {
      initialValue: editData?.ContactName || '',
      fieldName: 'ContactName',
      fieldLabel: 'Contact Name',
      fieldType: 'text',
      required: true
    },
    {
      initialValue: editData?.StateOfIncorporation || '',
      fieldName: 'StateOfIncorporation',
      fieldLabel: 'State of Incorporation',
      fieldType: 'text',
      required: true
    },
    {
      initialValue: editData?.DateOfIncorporation || '',
      fieldName: 'DateOfIncorporation',
      fieldLabel: 'Date of Incorporation',
      fieldType: 'date',
      required: true
    },
    {
      initialValue: editData?.AgentNameOfCorporation || '',
      fieldName: 'AgentNameOfCorporation',
      fieldLabel: 'Name of Registered Agent of corporation',
      fieldType: 'text',
      required: true
    },
    {
      initialValue: editData?.Purpose || '',
      fieldName: 'Purpose',
      fieldLabel: 'Purpose',
      fieldType: 'note'
    },
    {
      initialValue: editData?.ServiesTypesToSold || '',
      fieldName: 'ServiesTypesToSold',
      fieldLabel:
        'What are the types of goods, property or services to be sold ?',
      fieldType: 'note',
      required: true
    },
    {
      initialValue: editData?.ProcessOfSoldAndDeliver || '',
      fieldName: 'ProcessOfSoldAndDeliver',
      fieldLabel:
        'Describe the process in which goods are sold and delivered ? ',
      fieldType: 'note',
      required: true
    },
    {
      initialValue: editData?.VehicleUsed ? 'Yes' : 'No',
      fieldName: 'VehicleUsed',
      fieldLabel: 'Will a Vehicle be used ? ',
      fieldType: 'radio',
      required: true,
      options: [
        { label: 'Yes', value: 'Yes' },
        { label: 'No', value: 'No' }
      ],
      br: true,
      dependency: handleDependency,
      dependencyValue: 'Yes',
      dependencyFields: cloneData
    }
  ];

  const handleProceedToPay = async (data: any) => {
    data.VehicleUsed = data.VehicleUsed === 'Yes' ? true : false;
    if (!editData) {
      data.DocumentFileUpload = file;
    }
    if (!editData && cloneData.length > 0) {
      data.InspectionFee = Number(feeDetails?.inspectionFee);
    }
    const response = await httpRequest(
      editData ? 'PUT' : 'POST',
      editData
        ? `/solicitor_permit_application/update/${editData.ApplicationID}`
        : `/solicitor_permit_application/insert`,
      data
    );
    if (response?.status === 'success') {
      toast.success('Application submitted successfully.');
      let paymentSummary: PaymentSummary = {
        fees: Object.fromEntries(
          Object.entries(feeDetails).filter(([, value]) => value > 0)
        ),
        ...(taxRate > 0 ? { taxRate } : {})
      };
      if (!data.VehicleUsed) {
        paymentSummary = {
          fees: {
            applicationFee: paymentSummary.fees.applicationFee
          },
          taxRate: paymentSummary.taxRate
        };
      }

      const paymentApplicationDetails: PaymentApplicationDetails = {
        applicationDetails: {
          userName: `${userData?.FirstName} ${userData?.LastName}`,
          applicationNumber: null,
          applicationId:
            response?.data?.ApplicationID || editData?.ApplicationID,
          applicationStatus: 'Draft',
          statusPayload: 'In Review',
          permitPrefix: 'Solicitor'
        }
      };
      navigate(`/payment-methods`, {
        state: { paymentSummary, paymentApplicationDetails }
      });
    } else if (response?.type === 'NETWORK_ERROR') {
    } else {
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }
  };

  const handleInsertApplication = async (data: any, type = '') => {
    if (!file.length) {
      setDocsErrorMessege('At least one document is required.');
      return;
    }

    data.VehicleUsed = data.VehicleUsed === 'Yes' ? true : false;
    if (clickedOn !== 'Update' && !type) {
      data.DocumentFileUpload = file;
    }
    setLoading(true);
    setLoading(false);

    const response = await httpRequest(
      clickedOn === 'Update' || type ? 'PUT' : 'POST',
      clickedOn === 'Update' || type
        ? `/solicitor_permit_application/update/${editData.ApplicationID}`
        : `/solicitor_permit_application/insert`,
      data
    );
    if (response?.status === 'success') {
      if (type) {
        return true;
      }
      toast.success(
        `Application ${
          clickedOn === 'Update' ? 'update' : 'submitted'
        } successfully.`
      );

      if (
        clickedOn === 'Save Draft' ||
        editData.ApplicationStatus === 'Draft'
      ) {
        setLoading(false);
        navigate(`/${townshipId}/Clerk Department/${permit}/draft-application`);
      } else {
        navigate(`/${townshipId}/Clerk Department/${permit}/application-list`);
      }
    } else if (response?.type === 'NETWORK_ERROR') {
    } else {
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }
  };

  const handleApproveApplication = async () => {
    setOpenApprovePopup(false);
    setLoading(true);
    const payload = {
      ApplicationID: Number(editData.ApplicationID),
      ApplicationStatus: 'Approved',
      Purpose: editData?.Purpose || '',
      CompanyName: editData?.CompanyName || '',
      CompanyAddress: editData?.CompanyAddress || '',
      CompanyEmail: editData?.CompanyEmail || '',
      CompanyPhoneNumber: editData?.CompanyPhoneNumber || '',
      ContactName: editData?.ContactName || '',
      StateOfIncorporation: editData?.StateOfIncorporation || '',
      DateOfIncorporation: editData?.DateOfIncorporation || '',
      VehicleUsed: editData?.VehicleUsed || false,
      VehicleYear: editData?.VehicleYear || '',
      VehicleManufactured: editData?.VehicleManufactured || '',
      VehicleType: editData?.VehicleType || '',
      VehicleColor: editData?.VehicleColor || '',
      VehicleLisensePlateNumber: editData?.VehicleLisensePlateNumber || '',
      VehicleModel: editData?.VehicleModel || ''
    };
    const response = await httpRequest(
      'PUT',
      `/applications/${permit}/update_status`,
      payload
    );
    if (response?.status === 'success') {
      sendNotification(socket, {
        UserID: editData?.UserID, // receiver user ID --
        TownshipID: Number(townshipId),
        ApplicationID: editData?.ApplicationID,
        PermitID: Number(permit),
        Title: 'Application Approved',
        Message: `Solicitor application (${editData?.ApplicationNumber}) is approved.`,
        ApplicationNumber: editData?.ApplicationNumber
      });
      toast?.success(`Application Approved successfully.`);
      navigate(`/${townshipId}/Clerk Department/${permit}/application-list`);
    } else {
      toast?.error(response?.message);
      dispatch(handleError(response?.message));
    }
    setLoading(false);
  };

  const handleSubmit = async (data) => {
    if (clickedOn === 'Proceed to Payment') {
      handleProceedToPay(data);
    } else if (clickedOn === 'Save Draft' || clickedOn === 'Update') {
      handleInsertApplication(data);
    } else if (clickedOn === 'Approve') {
      const applicationUpdated = await handleInsertApplication(data, 'Approve');
      if (applicationUpdated) setOpenApprovePopup(true);
    }
  };

  const handleGetApplicationFee = async () => {
    setLoading(true);
    const response = await httpRequest(
      'GET',
      `/configuration/fee_details?PermitID=${permit}`
    );
    if (response?.status === 'success') {
      const feeObject = getFeesForCurrentPermit(response.data);
      setTaxRate(feeObject?.CreditCardFee || 0);
      setFeeDetails({
        applicationFee: Number(feeObject?.ApplicationFee),
        inspectionFee: Number(feeObject?.InspectionFee)
      });
      setLoading(false);
    } else if (response?.type === 'NETWORK_ERROR') {
    } else {
      setLoading(false);
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }
  };

  const getUploadedDocuments = async () => {
    const response = await httpRequest(
      'GET',
      `/uploadPDF/?ApplicationID=${applicationID}&PermitID=${permit}`
    );
    const data = await response?.data;
    if (response?.status === 'success') {
      const abstractedData =
        data &&
        data?.length &&
        data?.map((item) => {
          return {
            FileName: item?.DocumentName,
            Type: item.DocumentType,
            downloadURL: item.downloadURL,
            ApplicationDocsID: item.ApplicationDocsID
          };
        });
      setFile(abstractedData);
    } else {
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }
  };
  useEffect(() => {
    if (permit) {
      handleGetApplicationFee();
    }
    if (applicationID) getUploadedDocuments();
    return () => {
      editData = '';
    };
  }, []);

  const getFormButtons = () => {
    let buttonsCollection = ['Cancel'];
    if (editData?.ApplicationStatus) {
      if (
        editData?.ApplicationStatus === 'In Review' ||
        editData?.ApplicationStatus === 'Draft'
      ) {
        buttonsCollection = [...buttonsCollection, 'Update'];
      }
      if (editData?.ApplicationStatus === 'Draft') {
        buttonsCollection = [...buttonsCollection, 'Proceed to Payment'];
      }
      if (isManager && editData?.ApplicationStatus === 'In Review') {
        buttonsCollection = [...buttonsCollection, 'Approve'];
      }
    }
    return buttonsCollection;
  };
  return loading ? (
    <LoadingOverlay />
  ) : (
    <Container sx={{ mt: 2 }} className="main-table-container">
      <Breadcrumb
        PermitName={'Clerk Solicitor'}
        PageTitle={editData ? 'Edit  Application' : 'New Application'}
      />

      <FormHeaderGreeting
        name={`${userData?.FirstName} ${userData?.LastName}`}
        applicationType={editData ? 'edit' : 'new'}
        applicationStatus={editData?.ApplicationStatus}
        applicationNumber={editData?.ApplicationNumber}
      />

      {switchTo ? (
        <Content
          switchTo={switchTo}
          setSwitchTo={setSwitchTo}
          setIsOpenState={setIsOpen}
          applicationID={editData?.ApplicationID}
          UserID={userData?.UserID}
          ApplicationStatus={editData?.ApplicationStatus}
          ApplicationNumber={editData?.ApplicationNumber}
          selectMultipleFiles={true}
        />
      ) : (
        <Card>
          <Box
            m={3}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            {editData && editData?.ApplicationStatus !== 'Draft' ? (
              <>
                <Typography
                  color={
                    editData?.ApplicationStatus === 'Approved'
                      ? 'green'
                      : '#8B0000'
                  }
                  fontSize={15}
                  fontWeight={500}
                >
                  {editData?.ApplicationStatus === 'Approved'
                    ? `This application has been successfully approved ${
                        editData?.ApprovedDate &&
                        ` on ${format(
                          new Date(editData?.ApprovedDate),
                          'MM/dd/yyyy'
                        )}`
                      }.`
                    : editData?.ApplicationStatus === 'Rejected'
                    ? `This application has been rejected${
                        editData?.RejectedDate &&
                        ` on ${format(
                          new Date(editData?.RejectedDate),
                          'MM/dd/yyyy'
                        )}`
                      }.`
                    : ''}
                  {editData?.ApplicationStatus === 'Rejected' && (
                    <Tooltip
                      title={
                        'Reason of rejection -' +
                        ' ' +
                        editData?.ReasonOfRejection
                      }
                      arrow
                    >
                      <IconButton>
                        <InfoIcon color="primary" fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Typography>
              </>
            ) : (
              <Typography variant="h4">
                {editData ? 'Edit' : 'New'} Application
              </Typography>
            )}

            <PdfGenerator
              PermitName={'Clerk Solicitor'}
              allFields={allFields}
            />
          </Box>

          <Box m={{ md: 3, xs: 1 }}>
            <FormikForm
              allFields={allFields}
              innerRef={formRef}
              buttonPlacement={'right'}
              defaultEnabledButton={['Proceed to Payment', 'Approve', 'Update']}
              buttonTitle={
                editData
                  ? getFormButtons()
                  : ['Cancel', 'Save Draft', 'Proceed to Payment']
              }
              setClickedOn={setClickedOn}
              clickProp
              content={
                <>
                  <Box display={'flex'} gap={2}>
                    <TextField
                      id="outlined-basic"
                      disabled
                      sx={{ mt: 2 }}
                      value={`$ ${feeDetails?.applicationFee}.00`}
                      label="Application Fee"
                      variant="outlined"
                      InputLabelProps={{
                        className: 'weight-600'
                      }}
                    />

                    {cloneData.length > 0 && (
                      <TextField
                        id="outlined-basic"
                        disabled
                        sx={{ mt: 2 }}
                        label="Additional Fee"
                        value={`$ ${feeDetails?.inspectionFee}.00`}
                        variant="outlined"
                        InputLabelProps={{
                          className: 'weight-600'
                        }}
                      />
                    )}
                  </Box>
                  {editData && editData?.ApplicationStatus !== 'Draft' ? (
                    <Box sx={{ display: 'flex' }}>
                      <Content
                        switchTo={switchTo}
                        setSwitchTo={setSwitchTo}
                        setIsOpenState={setIsOpen}
                        applicationID={editData?.ApplicationID}
                        UserID={userData?.UserID}
                        ApplicationStatus={editData?.ApplicationStatus}
                        ApplicationNumber={editData?.ApplicationNumber}
                        selectMultipleFiles={true}
                      />
                    </Box>
                  ) : (
                    <>
                      <Button
                        sx={{ marginTop: 2 }}
                        variant="contained"
                        onClick={() => {
                          setIsOpen(true);
                        }}
                      >
                        Upload Documents
                      </Button>
                      <Typography sx={{ color: '#8B0000' }} m={1}>
                        {docsErrorMessege}
                      </Typography>
                    </>
                  )}
                </>
              }
              formMethod={handleSubmit}
              heading={'Clerk Department'}
              handleOtherButton={() => {
                navigate(`/dashboard/${permit}`);
              }}
            />
          </Box>
        </Card>
      )}

      <CustomModal
        title={'Upload documents'}
        IsOpen={IsOpen}
        setIsOpen={setIsOpen}
        loading={loading}
        actionButtons={['Save']}
        content={
          <UploadDocs
            ApplicationID={editData?.ApplicationID}
            file={file}
            setFile={setFile}
            setLoading={setLoading}
            reFetch={getUploadedDocuments}
            permitName={'Solicitor'}

          />
        }
      />
      <CustomModal
        title={'Approve Application?'}
        IsOpen={openApprovePopup}
        setIsOpen={setOpenApprovePopup}
        onSave={handleApproveApplication}
        actionButtons={['No', 'Yes']}
        loading={loading}
        content={
          <Typography>
            Are you sure you want to approve this application?
          </Typography>
        }
      />
    </Container>
  );
};

export default NewApplication;
